import {
  AccountBalanceWallet,
  ArrowBack as ArrowBackIcon,
  Replay as ReplayIcon,
  TaskAlt as TaskAltIcon
} from '@mui/icons-material';
import { Box, Tooltip, Button, CircularProgress, Stack } from '@mui/material';
import { useAtom } from 'jotai';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useInvoiceController } from 'api/controllers/InvoiceController';

import {
  DocumentType,
  EntryType,
  Invoice,
  InvoicePatch,
  InvoiceSection,
  InvoiceStages
} from 'openapi';

import { FileInvoiceApproval } from 'components/forms/InvoiceVerification/FileInvoiceApproval';
import { FileInvoiceVerification } from 'components/forms/InvoiceVerification/FileInvoiceVerification';
import { RevenueVerificationStepper } from 'components/forms/RevenueVerificationStepper/RevenueVerificationStepper';
import { Layout } from 'components/Layout/Layout';
import { ConfirmationDialog } from 'components/shared/Modal/ConfirmationDialog';

import { TokenExpirationModalContext } from 'context/TokenExpirationModalProvider';
import { useTranslations } from 'context/TranslationContext';

import { useModal } from 'hooks/useModal';

import { getConfigByDocumentType } from 'utils/config/documentEntries';
import {
  ACCESS_TOKEN,
  COMPANY_PARAMETER,
  ID_PARAMETER
} from 'utils/constants/constants';
import { FilterType } from 'utils/enums/FilterType';
import { InvoiceEventType } from 'utils/enums/Invoice';
import { getRouteForBackNavigation } from 'utils/helpers/filtersHelpers';
import { getEventData } from 'utils/helpers/serverSentEvents';
import { createSSEConnection } from 'utils/helpers/sseConnection';
import { AppRoutesEnum } from 'utils/routes';

import { verificationContentWrapper } from 'styles/pages/InvoiceVerificationStyle';

import { filterTypeAtom, isRequestPendingAtom, userAtom } from 'state/state';

import { CreatedDocumentVerificationStepper } from './CreatedDocumentVerificationStepper';
import { InvoiceFilePreview } from './InvoiceFilePreview';

export const InvoiceVerification = React.memo((): React.ReactElement => {
  const { id, companyId } = useParams();
  const navigate = useNavigate();

  const [filterType] = useAtom(filterTypeAtom);
  const [user] = useAtom(userAtom);
  const [, setRequestPending] = useAtom(isRequestPendingAtom);

  const {
    getSingleInvoiceById,
    rempromptInvoice,
    setIsAccounted,
    verifyInvoiceSection
  } = useInvoiceController();

  const {
    isOpen: isAccountModalOpen,
    openModal: openAccountModal,
    closeModal: closeAccountModal
  } = useModal();

  const { translate } = useTranslations();

  const [currentInvoice, setCurrentInvoice] = useState<Invoice | null>(null);
  const { isRefreshedToken, handleRefresh } = useContext(
    TokenExpirationModalContext
  );
  const [triggeredReprompt, setTriggeredReprompt] = useState<boolean>(false);

  const [token, setToken] = useState<string>();

  const companyIdNumber = useRef(Number(companyId));

  const isChangingCompany = useRef(false);

  const isCreatedDocument = !currentInvoice?.filePath;

  const getInvoice = useCallback(
    async (calledFromSSEs?: boolean) => {
      setRequestPending(true);
      try {
        const invoice = await getSingleInvoiceById(
          Number(companyIdNumber.current),
          Number(id)
        );
        if (
          invoice.stage === InvoiceStages.UPLOADED &&
          !triggeredReprompt &&
          !calledFromSSEs
        ) {
          navigate(`/companies/${companyId}/invoices`);
        }

        setCurrentInvoice(invoice);
        setTriggeredReprompt(false);
      } finally {
        setRequestPending(false);
      }
    },
    [companyId, id, getSingleInvoiceById, triggeredReprompt, navigate]
  );

  const handleInvoiceEvent = useCallback(
    async (data: string) => {
      const { eventType, eventId } = getEventData(data);
      if (eventType === InvoiceEventType.INVOICE_UPDATE && eventId === id) {
        if (isChangingCompany.current) return;
        getInvoice(true);
      }
    },
    [id, getInvoice]
  );

  const reprompt = useCallback(async () => {
    setTriggeredReprompt(true);
    await rempromptInvoice(currentInvoice?.id as number);
  }, [rempromptInvoice, currentInvoice]);

  const refreshInvoice = useCallback(
    async (newCompanyId: number) => {
      if (!currentInvoice?.id) return;
      setRequestPending(true);
      try {
        const invoice = await getSingleInvoiceById(
          newCompanyId,
          Number(currentInvoice.id)
        );
        setCurrentInvoice(invoice);
      } finally {
        setRequestPending(false);
      }
    },
    [currentInvoice, getSingleInvoiceById]
  );

  const handleCompanyChange = useCallback(
    async (receiverValues: InvoicePatch) => {
      const newCompanyId = receiverValues.companyId;
      if (!currentInvoice?.id || !currentInvoice.companyId || !newCompanyId) {
        return;
      }
      if (newCompanyId === currentInvoice.companyId) return;

      isChangingCompany.current = true;
      setRequestPending(true);
      try {
        await verifyInvoiceSection(
          currentInvoice.companyId,
          currentInvoice.id,
          InvoiceSection.COMPANY,
          receiverValues
        );

        const route =
          currentInvoice.entryType === EntryType.REVENUE
            ? AppRoutesEnum.REVENUE_VERIFICATION
            : AppRoutesEnum.INVOICE_VERIFICATION;

        navigate(
          route
            .replace(ID_PARAMETER, String(currentInvoice.id))
            .replace(COMPANY_PARAMETER, String(newCompanyId)),
          { replace: true }
        );

        await refreshInvoice(newCompanyId);
      } catch {
        // Request errors are surfaced by the interceptor (toast); keep the
        // current company when the switch fails.
      } finally {
        isChangingCompany.current = false;
        setRequestPending(false);
      }
    },
    [
      currentInvoice,
      verifyInvoiceSection,
      navigate,
      refreshInvoice,
      setRequestPending
    ]
  );

  const isRepromptDisabled = useMemo(
    () => currentInvoice?.stage !== InvoiceStages.DRAFT,
    [currentInvoice]
  );

  const handleNavigateBack = useCallback(() => {
    const route = getRouteForBackNavigation(
      localStorage.getItem('state') || '',
      filterType || FilterType.Initial,
      companyId
    );
    navigate(route);
  }, [navigate, filterType, companyId]);

  const confirmAccount = async () => {
    closeAccountModal();
    if (!currentInvoice?.id) return;
    await setIsAccounted([Number(currentInvoice.id)]);
    if (companyId) refreshInvoice(Number(companyId));
  };

  useEffect(() => {
    companyIdNumber.current = Number(companyId);
  }, [companyId]);

  useEffect(() => {
    if (!token) {
      return;
    }

    const cleanup = createSSEConnection({
      token,
      userId: user?.id,
      onEvent: handleInvoiceEvent,
      onRefresh: handleRefresh,
      onTokenUpdated: (newToken: string) => setToken(newToken)
    });

    return cleanup;
  }, [token]);

  useEffect(() => {
    getInvoice();
  }, [id]);

  useEffect(() => {
    setToken(localStorage.getItem(ACCESS_TOKEN) || '');
  }, [localStorage.getItem(ACCESS_TOKEN), isRefreshedToken]);

  if (!currentInvoice) {
    return (
      <Layout>
        <Stack alignItems="center" justifyContent="center" height="80vh">
          <CircularProgress />
        </Stack>
      </Layout>
    );
  }

  const renderInvoiceContent = () => {
    if (currentInvoice.stage === InvoiceStages.UPLOADED) {
      return (
        <Box
          sx={{
            width: { lg: '50%', xs: '100%' },
            textAlign: 'center'
          }}
        >
          <CircularProgress sx={{ mt: 4 }} />
        </Box>
      );
    }

    if (isCreatedDocument) {
      return (
        <CreatedDocumentVerificationStepper
          currentInvoice={currentInvoice}
          triggeredReprompt={triggeredReprompt}
          setCurrentInvoice={setCurrentInvoice}
          refreshInvoice={refreshInvoice}
        />
      );
    }

    if (currentInvoice.entryType === EntryType.REVENUE) {
      const documentConfig = getConfigByDocumentType(currentInvoice.entryType);

      return (
        <Box
          sx={{
            ...verificationContentWrapper,
            display: 'flex',
            gap: { xs: 7, lg: 2 },
            flexDirection: {
              lg: 'row',
              xs: 'column-reverse'
            },
            justifyContent: 'space-between'
          }}
        >
          <InvoiceFilePreview currentInvoice={currentInvoice} />

          <Box sx={{ width: { lg: '50%', xs: '100%' } }}>
            <RevenueVerificationStepper
              currentInvoice={currentInvoice}
              triggeredReprompt={triggeredReprompt}
              setCurrentInvoice={setCurrentInvoice}
              refreshInvoice={refreshInvoice}
              documentConfig={documentConfig}
            />
          </Box>
        </Box>
      );
    }

    if (currentInvoice.stage === InvoiceStages.DRAFT) {
      return (
        <FileInvoiceVerification
          currentInvoice={currentInvoice}
          onCompanyChange={handleCompanyChange}
          onInvoiceUpdated={setCurrentInvoice}
        />
      );
    }

    return (
      <FileInvoiceApproval
        currentInvoice={currentInvoice}
        setCurrentInvoice={setCurrentInvoice}
        refreshInvoice={refreshInvoice}
      />
    );
  };

  return (
    <Layout
      styles={{
        paddingTop: '1rem'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: { lg: '50%', md: '100%' },
          mb: 2
        }}
      >
        <Button startIcon={<ArrowBackIcon />} onClick={handleNavigateBack}>
          {translate('buttons.back')}
        </Button>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {currentInvoice.documentType !== DocumentType.NO_DOCUMENT &&
            (currentInvoice.stage === InvoiceStages.APPROVED ||
              currentInvoice.stage === InvoiceStages.FINALIZED) && (
              <Button
                onClick={openAccountModal}
                color="primary"
                disabled={currentInvoice.isBooked}
                startIcon={
                  currentInvoice.isBooked ? (
                    <TaskAltIcon
                      sx={{
                        width: '1.5rem',
                        height: '1.5rem',
                        mr: '0.2rem',
                        color: '#43a047'
                      }}
                    />
                  ) : (
                    <AccountBalanceWallet
                      sx={{ width: '1.5rem', height: '1.5rem', mr: '0.2rem' }}
                    />
                  )
                }
                sx={{
                  '&.Mui-disabled': {
                    color: 'rgb(0,0,0,0.87)'
                  }
                }}
              >
                {currentInvoice.isBooked
                  ? translate('labels.bookedStatus')
                  : translate('buttons.book')}
              </Button>
            )}

          {!isCreatedDocument && (
            <Tooltip
              title={
                isRepromptDisabled ? translate('messages.repromptDisabled') : ''
              }
            >
              <span>
                <Button
                  startIcon={<ReplayIcon />}
                  disabled={isRepromptDisabled}
                  onClick={reprompt}
                >
                  {translate('buttons.reprompt')}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      {renderInvoiceContent()}

      <ConfirmationDialog
        title={translate('titles.book')}
        isOpen={isAccountModalOpen}
        onClose={closeAccountModal}
        size="sm"
        onConfirm={confirmAccount}
      >
        {translate('messages.accountInvoiceConfirmation')}
      </ConfirmationDialog>
    </Layout>
  );
});
